import React from "react";
import { FileText, RotateCcw, RotateCw, Upload, X } from "react-feather";
import {
  PdfRenderer,
  PdfToolbarContext,
} from "../pdf-renderer/pdf-renderer";
import { PdfToolbar } from "../pdf-renderer/pdf-toolbar";
import { Button } from "../button/button";
import { InputField } from "../input-field/input-field";
import { SelectInput } from "../select-input/select-input";
import { UploadedFileBanner } from "../uploaded-file-banner/uploaded-file-banner";
import { UploadDropzone } from "./upload-dropzone";
import {
  COMPRESSION_TYPE_OPTIONS,
  COMPRESS_MAX_BYTES,
  CompressEntry,
  canCompressEntry,
  canDownloadEntry,
  formatByteSize,
} from "./compress-documents";
import type {
  PdfCompressionType,
  PdfMergePanelProps,
} from "./types";

export interface CompressWorkspaceProps {
  compressEntries: CompressEntry[];
  compressUploads: { id: string; fileName: string }[];
  onFilesSelected: (files: File[]) => void;
  onCancelUpload: (id: string) => void;
  activeCompressId: string;
  setActiveCompressId: (id: string) => void;
  compressingIds: ReadonlySet<string>;
  busyIds: ReadonlySet<string>;
  compressError: string | null;
  activeCompressEntry: CompressEntry | undefined;
  activeIsCompressing: boolean;
  activeIsBusy: boolean;
  activeCompressError: string | undefined;
  showUploadButton: boolean;
  compressAddDocumentLabel: string;
  showRotate: boolean;
  compressViewerContent?: React.ReactNode;
  pdfRendererProps?: PdfMergePanelProps["pdfRendererProps"];
  onUploadClick: () => void;
  onRemoveDocument: (id: string) => void;
  onUpdateEntry: (
    id: string,
    patch: Partial<
      Pick<
        CompressEntry,
        | "title"
        | "compressionType"
        | "customCompressMode"
        | "customCompressValue"
      >
    >,
  ) => void;
  onCompressRedo: () => void;
  onCompress: () => void;
  onCompressDownload: () => void;
  onRotateDocument: (id: string, degrees: number) => void;
  showDocumentRemoval?: boolean;
  onBack?: () => void;
  backLabel?: string;
}

export function CompressWorkspace({
  compressEntries,
  compressUploads,
  onFilesSelected,
  onCancelUpload,
  activeCompressId,
  setActiveCompressId,
  compressingIds,
  busyIds,
  compressError,
  activeCompressEntry,
  activeIsCompressing,
  activeIsBusy,
  activeCompressError,
  showUploadButton,
  compressAddDocumentLabel,
  showRotate,
  compressViewerContent,
  pdfRendererProps,
  onUploadClick,
  onRemoveDocument,
  onUpdateEntry,
  onCompressRedo,
  onCompress,
  onCompressDownload,
  onRotateDocument,
  showDocumentRemoval = true,
  onBack,
  backLabel = "Back",
}: CompressWorkspaceProps) {
  const showCompressAction = activeCompressEntry &&
    activeCompressEntry.compressionType !== "original" &&
    (!activeCompressEntry.compressedPdf || activeIsCompressing);

  const uploadBanners = compressUploads.map((upload) => (
    <div key={upload.id} role="status" aria-label={`Uploading ${upload.fileName}`}>
      <UploadedFileBanner
        fileName={upload.fileName}
        currentProgress={0}
        deleteUploadedFile={() => onCancelUpload(upload.id)}
      />
    </div>
  ));

  if (compressEntries.length === 0) {
    return (
      <section className="pdf-merge-panel__compress-empty" aria-label="Upload documents to compress">
        {compressUploads.length > 0 ? (
          <div className="pdf-merge-panel__upload-banners">{uploadBanners}</div>
        ) : showUploadButton ? (
          <UploadDropzone
            accept="application/pdf,.pdf"
            maxSizeBytes={COMPRESS_MAX_BYTES}
            onFilesSelected={onFilesSelected}
            onFileRejected={(file) => onFilesSelected([file])}
          />
        ) : (
          <p className="pdf-merge-panel__empty">Upload a document to begin.</p>
        )}
        {compressError && (
          <p className="pdf-merge-panel__compress-error" role="alert">{compressError}</p>
        )}
      </section>
    );
  }

  return (
    <>
      <aside className="pdf-merge-panel__sidebar" aria-label="Documents to compress">
        <div className="pdf-merge-panel__list">
          {uploadBanners}
          <div className="pdf-merge-panel__rows">
            {compressEntries.map((entry) => {
              const isActive = entry.id === activeCompressId;
              const entryIsCompressing = compressingIds.has(entry.id);
              return (
                <div
                  key={entry.id}
                  className={[
                    "pdf-merge-panel__row",
                    isActive && "pdf-merge-panel__row--active",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-current={isActive ? "true" : undefined}
                  aria-busy={entryIsCompressing || undefined}
                  onClick={() => setActiveCompressId(entry.id)}
                >
                  <button
                    type="button"
                    className="pdf-merge-panel__document-button"
                    tabIndex={isActive ? 0 : -1}
                    title={entry.fileName}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveCompressId(entry.id);
                    }}
                  >
                    <span className="pdf-merge-panel__document-name">
                      {entry.name}
                    </span>
                  </button>
                  {entryIsCompressing && (
                    <span className="pdf-merge-panel__row-status">
                      Compressing…
                    </span>
                  )}
                  {showDocumentRemoval && (
                    <button
                      type="button"
                      className="pdf-merge-panel__pending-remove"
                      aria-label={`Remove ${entry.name}`}
                      disabled={busyIds.has(entry.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveDocument(entry.id);
                      }}
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {showUploadButton && (
          <div className="pdf-merge-panel__compress-upload">
            <button
              type="button"
              className="pdf-merge-panel__compress-upload-button"
              onClick={onUploadClick}
            >
              <Upload size={12} aria-hidden="true" />
              {compressAddDocumentLabel}
            </button>
          </div>
        )}
        {compressError && (
          <p className="pdf-merge-panel__compress-error" role="alert">
            {compressError}
          </p>
        )}
      </aside>
      <section className="pdf-merge-panel__preview" aria-label="PDF preview">
        {activeCompressEntry && (
          <div className="pdf-merge-panel__file-bar">
            <div
              className="pdf-merge-panel__file-name"
              title={activeCompressEntry.fileName}
            >
              {activeCompressEntry.fileName}
            </div>
            <div
              className="pdf-merge-panel__file-size"
              title={`${activeCompressEntry.file.size} bytes`}
            >
              <FileText size={14} aria-hidden="true" />
              {formatByteSize(activeCompressEntry.file.size)}
            </div>
          </div>
        )}
        <div className="pdf-merge-panel__viewer">
          {activeIsCompressing && (
            <div
              className="pdf-merge-panel__viewer-loading"
              role="status"
              key={activeCompressEntry?.id}
            >
              Compressing…
            </div>
          )}
          {activeCompressEntry ? (
            <PdfRenderer
              {...pdfRendererProps}
              src={{
                buffer: activeCompressEntry.pdf,
                name: `${activeCompressEntry.id}-v${activeCompressEntry.pdfRevision}-${activeCompressEntry.fileName}`,
              }}
              toolbar={
                pdfRendererProps?.toolbar !== undefined || !showRotate
                  ? pdfRendererProps?.toolbar
                  : (ctx: PdfToolbarContext) => (
                      <PdfToolbar ctx={ctx}>
                        {[-90, 90].map((degrees) => (
                          <button
                            key={degrees}
                            type="button"
                            className="pdf-renderer__tool-btn"
                            aria-label={
                              degrees < 0
                                ? "Rotate 90° counter-clockwise"
                                : "Rotate 90° clockwise"
                            }
                            title={
                              degrees < 0
                                ? "Rotate 90° counter-clockwise"
                                : "Rotate 90° clockwise"
                            }
                            onClick={() => {
                              if (activeIsBusy) return;
                              onRotateDocument(activeCompressEntry.id, degrees);
                            }}
                            disabled={activeIsBusy}
                          >
                            {degrees < 0 ? (
                              <RotateCcw size={16} aria-hidden="true" />
                            ) : (
                              <RotateCw size={16} aria-hidden="true" />
                            )}
                          </button>
                        ))}
                      </PdfToolbar>
                    )
              }
              height="100%"
              width="100%"
            />
          ) : (
            <p className="pdf-merge-panel__empty pdf-merge-panel__empty--viewer">
              Select an uploaded document to preview it.
            </p>
          )}
        </div>
        <div className="pdf-merge-panel__viewer-slot">
          {!activeCompressEntry?.result && (
            <>
              <div className="pdf-merge-panel__compress-fields">
                <InputField
                  className="pdf-merge-panel__compress-title"
                  label="Document Title"
                  required
                  placeholder="Enter Title"
                  size="sm"
                  fullWidth
                  value={activeCompressEntry?.title ?? ""}
                  disabled={!activeCompressEntry || activeIsBusy}
                  onChange={(event) => {
                    if (!activeCompressEntry) return;
                    onUpdateEntry(activeCompressEntry.id, {
                      title: event.target.value,
                    });
                  }}
                />
                <div className="pdf-merge-panel__compress-field">
                  <span className="pdf-merge-panel__compress-field-label">
                    Compression Type
                    <span className="pdf-merge-panel__compress-required">*</span>
                  </span>
                  <SelectInput
                    className="pdf-merge-panel__compress-select"
                    placeholder="Select"
                    value={activeCompressEntry?.compressionType ?? ""}
                    options={COMPRESSION_TYPE_OPTIONS}
                    disabled={!activeCompressEntry || activeIsBusy}
                    onChange={(value) => {
                      if (!activeCompressEntry) return;
                      const compressionType = value as PdfCompressionType;
                      onUpdateEntry(activeCompressEntry.id, {
                        compressionType,
                        ...(compressionType === "custom"
                          ? { customCompressMode: "to_size" }
                          : { customCompressMode: "", customCompressValue: null }),
                      });
                    }}
                  />
                </div>
              </div>
              {activeCompressEntry?.compressionType === "custom" && (
                <div className="pdf-merge-panel__compress-fields pdf-merge-panel__compress-fields--custom">
                  <InputField
                    className="pdf-merge-panel__compress-title"
                    label="Size (MB)"
                    required
                    type="number"
                    inputMode="decimal"
                    min={0.01}
                    max={40}
                    step={0.1}
                    placeholder="Enter size in MB"
                    size="sm"
                    fullWidth
                    disabled={activeIsBusy}
                    value={activeCompressEntry.customCompressValue ?? ""}
                    onChange={(event) => {
                      const raw = event.target.value;
                      onUpdateEntry(activeCompressEntry.id, {
                        customCompressValue: raw === "" ? null : Number(raw),
                      });
                    }}
                  />
                </div>
              )}
            </>
          )}
          {compressViewerContent}
          {activeCompressError && (
            <p className="pdf-merge-panel__compress-error" role="alert">
              {activeCompressError}
            </p>
          )}
          {activeCompressEntry?.result && (
            <dl
              className={[
                "pdf-merge-panel__compress-result",
                activeCompressEntry.result.targetMet
                  ? "pdf-merge-panel__compress-result--met"
                  : "pdf-merge-panel__compress-result--missed",
              ].join(" ")}
            >
              <div>
                <dt>Compressed file size</dt>
                <dd>
                  {formatByteSize(activeCompressEntry.result.compressedFileSize)}
                </dd>
              </div>
              <div>
                <dt>Target met</dt>
                <dd>
                  {activeCompressEntry.result.targetMet ? "Yes" : "No"}
                </dd>
              </div>
            </dl>
          )}
          <div className="pdf-merge-panel__compress-actions">
            {onBack && (
              <Button
                buttonType="tertiary"
                size="medium"
                disabled={false}
                onClick={onBack}
              >
                {backLabel}
              </Button>
            )}
            {activeCompressEntry && activeCompressEntry.compressionType !== "original" && (
              <Button
                buttonType="secondary"
                size="medium"
                className=""
                disabled={activeIsBusy}
                onClick={onCompressRedo}
              >
                Redo
              </Button>
            )}
            {showCompressAction ? (
              <Button
                buttonType="primary"
                size="medium"
                className=""
                disabled={activeIsBusy || !canCompressEntry(activeCompressEntry)}
                onClick={onCompress}
              >
                {activeIsCompressing ? "Compressing…" : "Compress"}
              </Button>
            ) : (
              <Button
                buttonType="primary"
                size="medium"
                className=""
                disabled={activeIsBusy || !canDownloadEntry(activeCompressEntry)}
                onClick={onCompressDownload}
              >
                Download
              </Button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
